import { NavLink } from 'react-router-dom';
import Logo from '../assets/images/logo.svg';
import clsx from 'clsx';
import { useAppDispatch, useAppSelector } from '../hooks/redux-hooks';
import { useRef, useState } from 'react';
import {
  BookOpenText,
  Cable,
  ClipboardCheck,
  Database,
  Globe,
  GraduationCap,
  House,
  LibraryBig,
  Users,
  Power,
  User,
  Dice3,
  HandCoins,
  School,
} from 'lucide-react';
import useBrowserStorage from '../hooks/use-browser-storage';
import { useAuth0 } from '@auth0/auth0-react';
import { setAuthToken } from '../utils/set-token';
import {
  setAccessToken,
  setOrgInfo,
  setSessionId,
  setToastInfo,
} from '../store/common.slice';
import EnterpriseUserService from '../services/enterprise-data.service';
import { ToastType } from '../types/common.types';
import { TOrganisation } from '@answerr/types';

const liStyled = clsx(
  'group relative min-w-0 flex items-center rounded-[3px]',
  'transition cursor-pointer font-normal text-[14px] font-medium'
);

const navbarWrapperStyles = clsx(
  'bg-[#f3f3f3] dark:bg-[#141414] max-w-[240px] drop-shadow-md flex transition-all',
  'flex-col h-[98vh] justify-between shrink-0 w-[45px]',
  'overflow-hidden px-1 hover:w-[240px] text-[#18181B] border border-[#e1e1e1]',
  'rounded-md text-nowrap fixed top-[1vh] left-[5px] z-[1]'
);

const Navbar = () => {
  const { orgInfo, userInfo, isSuperAdmin } = useAppSelector(
    (state) => state.common
  );
  const [hovered, setHovered] = useState(false);
  const { removeItem, setItem } = useBrowserStorage();
  const { logout } = useAuth0();
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const imageRef = useRef<HTMLInputElement>(null);
  const signOut = () => {
    removeItem('orgInfo', 'local');
    removeItem('userInfo', 'local');
    removeItem('sessionId', 'local');
    removeItem('accessToken', 'local');
    setAuthToken('');
    setOrgInfo(null);
    setAccessToken('');
    setSessionId('');
    logout({
      logoutParams: { returnTo: window.location.origin },
    });
  };

  const onFileChange = async () => {
    try {
      if (imageRef.current?.files) {
        setLoading(true);
        const formData = new FormData();
        formData.append('img', imageRef?.current?.files[0]);
        const imageResponse =
          await EnterpriseUserService.updateCompanyLogo(formData);
        if (imageResponse?.data?.data?.url) {
          setLoading(false);
          dispatch(
            setToastInfo({
              isOpen: true,
              message: 'Image uploaded successfully',
              type: ToastType.SUCCESS,
            })
          );

          dispatch(
            setOrgInfo({
              ...(orgInfo as TOrganisation),
              companyLogo: imageResponse?.data?.data?.url || '',
            })
          );

          setItem(
            'orgInfo',
            JSON.stringify({
              ...orgInfo,
              companyLogo: imageResponse?.data?.data?.url || '',
            }),
            'local'
          );
          setImagePreview('');
        }
      }
    } catch (err) {
      console.log('Error: ', err);
    }
  };

  return (
    <div
      className={`${navbarWrapperStyles} ${hovered ? 'z-[2]' : ''}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="flex justify-between items-center mt-2">
        <div className="flex items-center gap-2 group">
          <div className="cursor-pointer ml-2">
            {orgInfo?.companyLogo ? (
              <div className={`${hovered ? '' : ''} w-6`}>
                <img
                  src={imagePreview || orgInfo?.companyLogo || ''}
                  alt="Company Logo"
                  className="h-6 w-6 rounded-full object-cover"
                />
              </div>
            ) : (
              <div className='className="py-1 px-1 font-semibold text-[16px] rounded-md bg-[#e7e7e9]"'>
                {orgInfo?.organisationName?.split('')[0]?.toUpperCase()}
              </div>
            )}
          </div>
          <input
            type="file"
            multiple={false}
            ref={imageRef}
            onChange={onFileChange}
            style={{ display: 'none' }}
          />
          <span
            className={
              hovered
                ? 'text-[15px] transition-all duration-200 opacity-100'
                : 'text-[15px] transition-all duration-200 opacity-0'
            }
          >
            {orgInfo?.organisationName}
          </span>
          <div
            className="
              group-hover:flex h-[40px] bg-[rgba(255,255,255,.9)] hidden w-full
              justify-center items-center text-[12px] absolute top-[0] left-0 cursor-pointer
            "
            onClick={() => imageRef.current?.click()}
          >
            Update Image
          </div>
        </div>
        <div title="Logout" onClick={signOut} style={{ zIndex: 2 }}>
          <Power color="red" size={16} className="mr-1 cursor-pointer" />
        </div>
      </div>
      <div className="w-full grow pt-3">
        <ul className="mt-4 mb-4">
          <li className={liStyled}>
            <NavLink
              to="/dashboard"
              className={({ isActive }) =>
                `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                  isActive ? 'bg-[#e7e7e9]' : ''
                }`
              }
            >
              <div className="flex items-center gap-2">
                <House color="black" size={16} style={{ flexShrink: 0 }} />
                <span
                  className={
                    hovered
                      ? 'transition-all duration-200 opacity-100'
                      : 'transition-all duration-200 opacity-0'
                  }
                >
                  Dashboard
                </span>
              </div>
            </NavLink>
          </li>

          {orgInfo?.allowAgent && (
            <li className={liStyled}>
              <NavLink
                to="/agents"
                className={({ isActive }) =>
                  `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                    isActive ? 'bg-[#e7e7e9]' : ''
                  }`
                }
              >
                <div className="flex items-center gap-2">
                  <Database color="black" size={16} style={{ flexShrink: 0 }} />
                  <span
                    className={
                      hovered
                        ? 'transition-all duration-200 opacity-100'
                        : 'transition-all duration-200 opacity-0'
                    }
                  >
                    Agents
                  </span>
                </div>
              </NavLink>
            </li>
          )}

          {orgInfo?.allowAssistant && (
            <li className={liStyled}>
              <NavLink
                to="/assistants"
                className={({ isActive }) =>
                  `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                    isActive ? 'bg-[#e7e7e9]' : ''
                  }`
                }
              >
                <div className="flex items-center gap-2">
                  <LibraryBig
                    color="black"
                    size={16}
                    style={{ flexShrink: 0 }}
                  />
                  <span
                    className={
                      hovered
                        ? 'transition-all duration-200 opacity-100'
                        : 'transition-all duration-200 opacity-0'
                    }
                  >
                    Assistants
                  </span>
                </div>
              </NavLink>
            </li>
          )}

          {orgInfo?.allowAIQ && (
            <li className={liStyled}>
              <NavLink
                to="/aiq"
                className={({ isActive }) =>
                  `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                    isActive ? 'bg-[#e7e7e9]' : ''
                  }`
                }
              >
                <div className="flex items-center gap-2">
                  <i className="icon-aiq text-[17px]" />
                  <span
                    className={
                      hovered
                        ? 'transition-all duration-200 opacity-100'
                        : 'transition-all duration-200 opacity-0'
                    }
                  >
                    AIQ
                  </span>
                </div>
              </NavLink>
            </li>
          )}

          {orgInfo?.allowACRA && (
            <li className={liStyled}>
              <NavLink
                to="/acra"
                className={({ isActive }) =>
                  `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                    isActive ? 'bg-[#e7e7e9]' : ''
                  }`
                }
              >
                <div className="flex items-center gap-2">
                  <ClipboardCheck
                    color="black"
                    size={16}
                    style={{ flexShrink: 0 }}
                  />
                  <span
                    className={
                      hovered
                        ? 'transition-all duration-200 opacity-100'
                        : 'transition-all duration-200 opacity-0'
                    }
                  >
                    ACRA
                  </span>
                </div>
              </NavLink>
            </li>
          )}

          {orgInfo?.allowAiTutor && (
            <li className={liStyled}>
              <NavLink
                to="/ai-tutors"
                className={({ isActive }) =>
                  `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                    isActive ? 'bg-[#e7e7e9]' : ''
                  }`
                }
              >
                <div className="flex items-center gap-2">
                  <BookOpenText
                    color="black"
                    size={16}
                    style={{ flexShrink: 0 }}
                  />
                  <span
                    className={
                      hovered
                        ? 'transition-all duration-200 opacity-100'
                        : 'transition-all duration-200 opacity-0'
                    }
                  >
                    AI Tutors
                  </span>
                </div>
              </NavLink>
            </li>
          )}

          {orgInfo?.allowQuiz && (
            <li className={liStyled}>
              <NavLink
                to="/quiz"
                className={({ isActive }) =>
                  `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                    isActive ? 'bg-[#e7e7e9]' : ''
                  }`
                }
              >
                <div className="flex items-center gap-2">
                  <Dice3 color="black" size={16} style={{ flexShrink: 0 }} />
                  <span
                    className={
                      hovered
                        ? 'transition-all duration-200 opacity-100'
                        : 'transition-all duration-200 opacity-0'
                    }
                  >
                    Quiz
                  </span>
                </div>
              </NavLink>
            </li>
          )}

          {orgInfo?.allowGrading && (
            <li className={liStyled}>
              <NavLink
                to="/grading"
                className={({ isActive }) =>
                  `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                    isActive ? 'bg-[#e7e7e9]' : ''
                  }`
                }
              >
                <div className="flex items-center gap-2">
                  <GraduationCap
                    color="black"
                    size={16}
                    style={{ flexShrink: 0 }}
                  />
                  <span
                    className={
                      hovered
                        ? 'transition-all duration-200 opacity-100'
                        : 'transition-all duration-200 opacity-0'
                    }
                  >
                    Grading
                  </span>
                </div>
              </NavLink>
            </li>
          )}

          <li className={liStyled}>
            <NavLink
              to="/enterprise-users"
              className={({ isActive }) =>
                `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                  isActive ? 'bg-[#e7e7e9]' : ''
                }`
              }
            >
              <div className="flex items-center gap-2">
                <User color="black" size={16} style={{ flexShrink: 0 }} />
                <span
                  className={
                    hovered
                      ? 'transition-all duration-200 opacity-100'
                      : 'transition-all duration-200 opacity-0'
                  }
                >
                  Users
                </span>
              </div>
            </NavLink>
          </li>

          <li className={liStyled}>
            <NavLink
              to="/user-groups"
              className={({ isActive }) =>
                `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                  isActive ? 'bg-[#e7e7e9]' : ''
                }`
              }
            >
              <div className="flex items-center gap-2">
                <Users color="black" size={16} style={{ flexShrink: 0 }} />
                <span
                  className={
                    hovered
                      ? 'transition-all duration-200 opacity-100'
                      : 'transition-all duration-200 opacity-0'
                  }
                >
                  User Groups
                </span>
              </div>
            </NavLink>
          </li>

          {/* <li className={liStyled}>
            <NavLink
              to="/ai-models"
              className={({ isActive }) =>
                `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                  isActive ? 'bg-[#e7e7e9]' : ''
                }`
              }
            >
              <div className="flex items-center gap-2">
                <Bot color="black" size={16} style={{ flexShrink: 0 }} />
                <span
                  className={
                    hovered
                      ? 'transition-all duration-200 opacity-100'
                      : 'transition-all duration-200 opacity-0'
                  }
                >
                  AI Models
                </span>
              </div>
            </NavLink>
          </li> */}

          {orgInfo?.allowConnector && (
            <li className={liStyled}>
              <NavLink
                to="/connectors"
                className={({ isActive }) =>
                  `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                    isActive ? 'bg-[#e7e7e9]' : ''
                  }`
                }
              >
                <div className="flex items-center gap-2">
                  <Cable color="black" size={16} style={{ flexShrink: 0 }} />
                  <span
                    className={
                      hovered
                        ? 'transition-all duration-200 opacity-100'
                        : 'transition-all duration-200 opacity-0'
                    }
                  >
                    Connectors
                  </span>
                </div>
              </NavLink>
            </li>
          )}

          {orgInfo?.allowLMS && isSuperAdmin && (
              <li className={liStyled}>
                <NavLink
                  to="/lms"
                  className={({ isActive }) =>
                    `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                      isActive ? 'bg-[#e7e7e9]' : ''
                    }`
                  }
                >
                  <div className="flex items-center gap-2">
                    <School color="black" size={16} style={{ flexShrink: 0 }} />
                    <span
                      className={
                        hovered
                          ? 'transition-all duration-200 opacity-100'
                          : 'transition-all duration-200 opacity-0'
                      }
                    >
                      LMS
                    </span>
                  </div>
                </NavLink>
              </li>
            )}

          {orgInfo?.makePublicAgents && (
            <li className={liStyled}>
              <NavLink
                to="/public-assistants"
                className={({ isActive }) =>
                  `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                    isActive ? 'bg-[#e7e7e9]' : ''
                  }`
                }
              >
                <div className="flex items-center gap-2">
                  <Globe color="black" size={16} style={{ flexShrink: 0 }} />
                  <span
                    className={
                      hovered
                        ? 'transition-all duration-200 opacity-100'
                        : 'transition-all duration-200 opacity-0'
                    }
                  >
                    Public Assistants
                  </span>
                </div>
              </NavLink>
            </li>
          )}

          {orgInfo?.allowTokenMgmt && (
            <li className={liStyled}>
              <NavLink
                to="/token-management"
                className={({ isActive }) =>
                  `w-full py-2 px-2.5 hover:bg-[#e7e7e9] rounded-md ${
                    isActive ? 'bg-[#e7e7e9]' : ''
                  }`
                }
              >
                <div className="flex items-center gap-2">
                  <HandCoins
                    color="black"
                    size={16}
                    style={{ flexShrink: 0 }}
                  />
                  <span
                    className={
                      hovered
                        ? 'transition-all duration-200 opacity-100'
                        : 'transition-all duration-200 opacity-0'
                    }
                  >
                    Token Management
                  </span>
                </div>
              </NavLink>
            </li>
          )}
        </ul>
      </div>
      <div className={`flex items-center gap-1 mb-4`}>
        {/* <img src={Logo} className={`w-[20px] h-[20px] ml-2`} alt="logo" /> */}
        <div className="ml-2 mr-1">
          <User color="black" size={16} style={{ flexShrink: 0 }} />
        </div>
        <span
          className={`text-[12px] transition-all duration-200 ${
            hovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {userInfo?.email ? userInfo.email : orgInfo?.email}
        </span>
      </div>
      <div
        className={`flex items-center gap-1 mb-1 rounded-md bg-appTextLight/[0.1] py-2`}
      >
        <img src={Logo} className={`w-[20px] h-[20px] ml-2`} alt="logo" />
        <span
          className={`text-[11px] transition-all duration-200 ${
            hovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          Powered by Answerr
        </span>
      </div>
    </div>
  );
};

export default Navbar;
